/**
 * Supabase Functions Error Handling Utilities
 *
 * When Supabase Functions returns a non-2xx status code, the client:
 * - Sets data = null
 * - Puts the Response object in error.context
 * - The response body must be parsed from the ReadableStream
 */

import { CREDIT_ERROR_CODES } from "@/constants/credit";

export const CUI_ARCHIVED_ERROR_CODE = "CUI_ARCHIVED";

/**
 * Structured error for insufficient credits
 */
export class InsufficientCreditsError extends Error {
  code = "INSUFFICIENT_CREDITS";
  shortage: number;
  required: number;
  current: number;

  constructor(data: {
    shortage: number;
    required: number;
    current: number;
    message?: string;
  }) {
    super(data.message || "Insufficient credits");
    this.name = "InsufficientCreditsError";
    this.shortage = data.shortage;
    this.required = data.required;
    this.current = data.current;
  }
}

/**
 * Structured error when the server rejects a session-create because the
 * CUI matches an existing archived session for the same user.
 */
export class CuiArchivedError extends Error {
  code = CUI_ARCHIVED_ERROR_CODE;
  archivedSessionId?: string;
  cui?: string;

  constructor(data: {
    cui?: string;
    archivedSessionId?: string;
    message?: string;
  }) {
    super(data.message || "CUI belongs to an archived session");
    this.name = "CuiArchivedError";
    this.cui = data.cui;
    this.archivedSessionId = data.archivedSessionId;
  }
}

/**
 * Parse error response from Supabase Functions
 *
 * When a Supabase Function returns a non-2xx status, the error object structure is:
 * {
 *   name: "FunctionsHttpError",
 *   context: Response (with body as ReadableStream)
 * }
 *
 * This function extracts and parses the response body.
 *
 * @param error - The error object from supabase.functions.invoke()
 * @returns Parsed error body or null if not parseable
 */
export async function parseSupabaseFunctionError(
  error: any
): Promise<any | null> {
  if (!error) return null;

  // Extract Response object from error.context
  const response = error.context;

  // If we have a Response object with a json() method, parse it
  if (response && typeof response.json === "function") {
    try {
      const errorBody = await response.json();
      return errorBody;
    } catch (parseError) {
      console.error(
        "Failed to parse Supabase Function error response:",
        parseError
      );
      return null;
    }
  }

  return null;
}

/**
 * Handle Supabase Functions error with automatic insufficient credits detection
 *
 * Usage:
 * ```typescript
 * const { data, error } = await supabase.functions.invoke("api/some-endpoint", {...});
 * await handleSupabaseFunctionError(error);  // Throws if error
 * // Continue with data...
 * ```
 *
 * @param error - The error object from supabase.functions.invoke()
 * @throws {InsufficientCreditsError} If error is insufficient credits
 * @throws {Error} For other errors
 */
export async function handleSupabaseFunctionError(error: any): Promise<void> {
  if (!error) return;

  console.error("Supabase Function error:", error);

  // Try to parse the error response body
  const errorBody = await parseSupabaseFunctionError(error);

  if (errorBody) {
    console.warn("Error response body:", errorBody);

    // Check if this is an insufficient credits error
    if (errorBody.error === CREDIT_ERROR_CODES.INSUFFICIENT_CREDITS) {
      throw new InsufficientCreditsError({
        shortage: errorBody.shortage,
        required: errorBody.required,
        current: errorBody.current,
        message: errorBody.message,
      });
    }

    if (errorBody.error === CUI_ARCHIVED_ERROR_CODE) {
      throw new CuiArchivedError({
        cui: errorBody.cui,
        archivedSessionId: errorBody.archived_session_id,
        message: errorBody.message,
      });
    }

    // For other error responses, throw with the error message
    throw new Error(errorBody.message || errorBody.error || "Request failed");
  }

  // For errors without a parseable response (network issues, etc.), throw as-is
  throw error;
}
